// src/pages/News/NewsPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { getNews, deleteNews, createNews } from "./../../services/newsServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import Button from "./../../components/Button/Button";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import NewsCard from "./NewsCard";
import CreateNewsModal from "./CreateNewsModal";
import EditNewsModal from "./EditNewsModal";
import NewsDetailsModal from "./NewsDetailsModal";
import { ToastContainer, toast } from "react-toastify";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { FaPlus, FaEye, FaEyeSlash, FaSearch } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const NewsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isAdmin =
    userRole === "Administrateur" ||
    userRole === "Bureau" ||
    userRole === "Super Admin";

  const [newsList, setNewsList] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const data = await getNews();
        setNewsList(data);
      } catch (error) {
        console.error("Failed to fetch news", error);
        toast.error("Erreur lors du chargement des actualités.");
      }
    };
    fetchNews();
  }, []);

  const refreshNewsList = async () => {
    try {
      const updatedNewsList = await getNews();
      setNewsList(updatedNewsList);
      return updatedNewsList;
    } catch (error) {
      console.error("Failed to refresh news", error);
      throw error;
    }
  };

  const handleAddNewsClick = () => {
    setCurrentNews(null);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateSuccess = async (newsData) => {
    try {
      await createNews(newsData);
      closeCreateModal();
      await refreshNewsList();
      toast.success("Actualité créée avec succès");
    } catch (error) {
      console.error("Erreur lors de la création de l'actualité:", error);
      toast.error("Erreur lors de la création de l'actualité.");
    }
  };

  const handleEditNews = (news) => {
    if (!isAdmin) return;
    setCurrentNews(news);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentNews(null);
  };

  const handleEditSuccess = async () => {
    closeEditModal();
    await refreshNewsList();
    toast.success("Actualité mise à jour avec succès");
  };

  const handleDeleteNews = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("Supprimer définitivement cette actualité ?")) return;

    try {
      await deleteNews(id);
      setNewsList((prev) => prev.filter((news) => news.id !== id));
      toast.success("Actualité supprimée avec succès");
    } catch (error) {
      console.error("Failed to delete news", error);
      toast.error("Erreur lors de la suppression de l'actualité");
    }
  };

  const openDetails = (news) => {
    setSelectedNews(news);
    setIsDetailsOpen(true);
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setSelectedNews(null);
  };

  const stripHtml = (html = "") =>
    html
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const filteredNews = useMemo(() => {
    let list = [...newsList];

    // 1) Actives / inactives selon rôle + toggle
    if (!isAdmin) {
      list = list.filter((n) => n.is_active);
    } else if (!showInactive) {
      list = list.filter((n) => n.is_active);
    }

    // 2) Recherche texte
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      list = list.filter((n) => {
        const plainContent = stripHtml(n.content || "").toLowerCase();
        return n.title.toLowerCase().includes(t) || plainContent.includes(t);
      });
    }

    // 3) Tri par date décroissante
    return list.sort(
      (a, b) =>
        new Date(b.created_at || b.updated_at || 0).getTime() -
        new Date(a.created_at || a.updated_at || 0).getTime()
    );
  }, [newsList, isAdmin, showInactive, searchTerm]);

  return (
    <div className="min-h-screen flex flex-col w-full mx-auto pt-[130px] sm:pt-[130px] md:pt-[150px] lg:pt-[150px] xl:pt-[150px] pb-10 bg-slate-50 dark:bg-slate-900 dark:text-white">
      <ScrollIndicator />

      {/* En-tête */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          eyebrow="Visiteurs & Utilisateurs"
          title="Actualités & Résultats"
          subtitle="Toutes les actualités du Comité, les infos importantes et les résultats hors département de nos judokas girondins."
          align="center"
          size="lg"
        />

        <InfoBubble title="Bienvenue sur la page Actualités & Résultats">
          <p className="text-sm">
            Parcourez les articles publiés par le Comité ou, si vous faites
            partie du Bureau, créez et mettez à jour les actualités directement
            depuis cette page.
          </p>
        </InfoBubble>
      </div>

      {/* Barre d’actions & filtres */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg">
          <div className="h-1.5 bg-gradient-to-r from-primary via-indigo-500 to-cyan-400" />
          <div className="p-4 md:p-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* Gauche : boutons admin */}
            <div className="flex flex-wrap gap-2">
              {isAdmin && (
                <>
                  <Button
                    text="Ajouter une actualité"
                    icon={FaPlus}
                    onClick={handleAddNewsClick}
                    className="gap-2 text-sm"
                  />
                  <Button
                    text={
                      showInactive
                        ? "Masquer les actualités archivées"
                        : "Voir aussi les archivées"
                    }
                    icon={showInactive ? FaEyeSlash : FaEye}
                    onClick={() => setShowInactive((prev) => !prev)}
                    className="gap-2 text-sm"
                  />
                </>
              )}
            </div>

            {/* Droite : recherche */}
            <div className="w-full md:max-w-sm">
              <label className="text-[11px] uppercase font-semibold tracking-[0.18em] text-slate-500 dark:text-slate-300 mb-1 block">
                Recherche
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  type="text"
                  placeholder="Titre, contenu, mot-clé..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/70"
                />
              </div>
              <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                {filteredNews.length} actualité(s) affichée(s).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Liste des actualités */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {filteredNews.length === 0 ? (
          <div className="text-center text-sm text-slate-500 dark:text-slate-300 mt-8">
            Aucune actualité ne correspond à vos critères pour le moment.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filteredNews.map((news) => (
              <NewsCard
                key={news.id}
                news={news}
                canModify={isAdmin}
                onEdit={() => handleEditNews(news)}
                onDelete={() => handleDeleteNews(news.id)}
                onOpenDetails={() => openDetails(news)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modales */}
      <CreateNewsModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onCreateSuccess={handleCreateSuccess}
      />

      <EditNewsModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        refreshList={handleEditSuccess}
        news={currentNews}
      />

      <NewsDetailsModal
        isOpen={isDetailsOpen}
        onClose={closeDetails}
        news={selectedNews}
      />

      <ToastContainer />
    </div>
  );
};

export default NewsPage;
